"use client";

import React from "react";
import { BlurFade } from "@/components/ui/blur-fade";
import { Shield, Zap, Lock, EyeOff, XCircle, Smartphone, Globe } from "lucide-react";
import Image from "next/image";

const ICON_MAP = {
  Shield,
  Zap,
  Lock,
  EyeOff,
  XCircle,
  Smartphone,
  Globe,
};

interface Benefit {
  title: string;
  description: string;
  icon?: string;
  image?: string;
}

interface BenefitsGridProps {
  title: string;
  items: Benefit[];
}

const BenefitsGrid = ({ title, items }: BenefitsGridProps) => {
  return (
    <section className="py-24 px-6 bg-muted">
      <div className="container max-w-6xl mx-auto flex flex-col items-center gap-16">
        {/* Header */}
        <BlurFade delay={0.1}>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground text-center">
            {title}
          </h2>
        </BlurFade>

        {/* Benefits Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 w-full">
          {items.map((benefit, i) => {
            const Icon = ICON_MAP[benefit.icon as keyof typeof ICON_MAP];
            return (
              <BlurFade key={i} delay={0.2 + i * 0.05}>
                <div className="flex flex-col items-center text-center gap-8 bg-white p-10 rounded-[2.5rem] border border-transparent hover:border-zinc-200 transition-all h-full shadow-sm hover:shadow-md">
                  {/* Card Image */}
                  <div className="w-full aspect-square max-w-[160px] bg-muted rounded-full flex items-center justify-center relative overflow-hidden">
                    {benefit.image ? (
                      <Image
                        src={benefit.image}
                        alt={benefit.title}
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <>
                        <div className="absolute inset-0 border-2 border-dashed border-zinc-200 rounded-full m-4" />
                        <div className="relative z-10 text-foreground/20">
                          {Icon && <Icon size={48} strokeWidth={1.5} />}
                        </div>
                      </>
                    )}
                  </div>

                <div className="space-y-4">
                  <h3 className="text-xl font-bold text-foreground">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground/60 font-medium leading-relaxed">
                    {benefit.description}
                  </p>
                </div>
              </div>
            </BlurFade>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default BenefitsGrid;

